import React, { useState, useContext, useEffect, useRef } from "react";
import UserDetail from "./UserDetail";
import { AuthContext } from "../../context/AuthContext";
import { GeneralContext } from "../../context/GeneralContext";
import UserData from "./UserData";
import { api } from "../../utils/authActions";
import Package from "./Package";
import { Button } from "@mui/material";
import Modal from "react-modal";
import { getPackageFromList } from "./adminLogic";
import ChangesCheck from "./ChangesCheck";
import "./adminComponents.scss";
import ConfirmationScreen from "../commonComponents/ConfirmationScreen";
import {
  adjustModalPositionAndSize,
  vhToPixels,
  vwToPixels,
  hasProperty,
} from "../../utils/helpers";

function EditUser(props) {
  const { userData, handleUserDelete } = props;
  const { authToken, packages, currentUser } = useContext(AuthContext);
  const { recordedErrorLog } = useContext(GeneralContext);
  const [fullName, setFullName] = useState(userData ? userData.full_name : "");
  const [email, setEmail] = useState(userData ? userData.email : "");
  const [newsLetter, setNewsletter] = useState(
    userData ? userData.newsletter : false
  );
  const [activePacks, setActivePacks] = useState([]);
  const [changesList, setChangesList] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [modalPlaceAndSize, setModalPlaceAndSize] = useState({
    top: "0",
    left: "0",
    height: "300px",
    width: "300px",
  });
  const [isDelUserOpen, setIsDelUserOpen] = useState(false);
  const [isModifyPackOpen, setIsModifyPackOpen] = useState(false);
  const [isDeactivatePackOpen, setIsDeactivatePackOpen] = useState(false);
  const [packToModify, setPackToModify] = useState(null);
  const [packToDeactivate, setPackToDeactivate] = useState(null);
  const [isAdmin, setIsAdmin] = useState(
    userData ? userData.is_superuser : false
  );
  const [isAcademicVerified, setIsAcademicVerified] = useState(
    userData ? userData.academic_verified : false
  );
  const [analysisSupportHoursLeft, setAnalysisSupportHoursLeft] = useState(
    userData ? (userData.analysis_support_hours_left !== null ? userData.analysis_support_hours_left : 0) : 0
  );
  const [stripePaymentAllowed, setStripePaymentAllowed] = useState(
    userData ? (userData.stripe_payment_allowed ?? false) : false
  );
  const detailRef = useRef();
  const dangerZoneRef = useRef();
  const modifyPackRef = useRef();
  const deactivatePackRef = useRef();

  useEffect(() => {
    // admin: read user packages for selected user
    api
      .getUserPackages(authToken, userData.id)
      .then((response) => {
        setActivePacks(response.data);
      })
      .catch((err) => {
        const errorDetails = hasProperty(err, "response")
          ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
          : "No backend response available";
        recordedErrorLog(
          `THERE WAS AN ERROR WITH USER PACKAGES: ${errorDetails}`,
          err,
          true,
          "Unexpected error occurred optaining available packages. Check console log for more details."
        );
      });
  }, []);

  useEffect(() => {
    setChangesList(produceChangeList());
  }, [email, newsLetter, fullName, isAdmin, isAcademicVerified, analysisSupportHoursLeft, stripePaymentAllowed]);

  const startUpdate = () => {
    try {
      const modalPosition = {
        top: vhToPixels(35),
        right: "auto",
        left: vwToPixels(50),
      };
      const modalSize = { width: 550, height: 650 };

      const adjusted = adjustModalPositionAndSize(modalPosition, modalSize);

      setModalPlaceAndSize(adjusted);
      setIsConfirmOpen(true);
    } catch (error) {
      recordedErrorLog("Update starting has failed: ", error);
    }
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmOpen(false);
  };

  const produceChangeList = () => {
    try {
      const list = [];

      if (email != null && email !== userData.email) {
        list.push({
          title: "Email",
          prev: userData.email,
          new: email,
        });
      }
      if (fullName != null && fullName !== userData.full_name) {
        list.push({
          title: "Full Name",
          prev: userData.full_name,
          new: fullName,
        });
      }
      if (newsLetter != null && newsLetter !== userData.newsletter) {
        list.push({
          title: "Newsletter",
          prev: userData.newsletter ? "Accepted" : "Not Accepted",
          new: newsLetter ? "Accepted" : "Not Accepted",
        });
      }
      if (isAdmin != null && isAdmin !== userData.is_superuser) {
        list.push({
          title: "Elavated Rights",
          prev: userData.is_superuser ? "Admin" : "Not Admin",
          new: isAdmin ? "Admin" : "Not Admin",
        });
      }
      if (isAcademicVerified != null && isAcademicVerified !== userData.academic_verified) {
        list.push({
          title: "Academic Verification",
          prev: userData.academic_verified ? "Verified" : "Not Verified",
          new: isAcademicVerified ? "Verified" : "Not Verified",
        });
      }
      if (analysisSupportHoursLeft != null && analysisSupportHoursLeft !== (userData.analysis_support_hours_left !== null ? userData.analysis_support_hours_left : 0)) {
        list.push({
          title: "Analysis Hours Left",
          prev: userData.analysis_support_hours_left !== null ? userData.analysis_support_hours_left : 0,
          new: analysisSupportHoursLeft,
        });
      }
      if (stripePaymentAllowed != null && stripePaymentAllowed !== userData.stripe_payment_allowed) {
        list.push({
          title: "Stripe Payment Allowed",
          prev: userData.stripe_payment_allowed ? "Allowed" : "Not Allowed",
          new: stripePaymentAllowed ? "Allowed" : "Not Allowed",
        });
      }

      return list;
    } catch (error) {
      recordedErrorLog(
        "Changes list production has encountered a failure: ",
        error
      );
      return [];
    }
  };

  const reverseChanges = () => {
    try {
      setEmail(userData.email);
      setFullName(userData.full_name);
      setNewsletter(userData.newsletter);
      setIsAdmin(userData.is_superuser);
      setIsAcademicVerified(userData.academic_verified);
      setAnalysisSupportHoursLeft(userData.analysis_support_hours_left !== null ? userData.analysis_support_hours_left : 0);
      setStripePaymentAllowed(userData.stripe_payment_allowed);
      setIsConfirmOpen(false);
    } catch (error) {
      recordedErrorLog("Changes reversal has failed: ", error);
    }
  };

  const approveChanges = () => {
    try {
      const payload = {
        ...userData,
        full_name: fullName,
        email: email,
        newsletter: newsLetter,
        is_superuser: isAdmin,
        academic_verified: isAcademicVerified,
        analysis_support_hours_left: analysisSupportHoursLeft,
        stripe_payment_allowed: stripePaymentAllowed ?? false,
      };
      // admin: update user
      api
        .updateUser(authToken, payload, userData.id)
        .then(() => {
          //function can receive 'response' here if needed
          setChangesList([]);
        })
        .catch((err) => {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH USER UPDATE: ${errorDetails}`,
            err,
            true,
            "Unexpected error occurred updating your profile details. Check console log for more details."
          );
        });

      setIsConfirmOpen(false);
    } catch (error) {
      recordedErrorLog("Changes approval has failed: ", error);
    }
  };

  function getCurrentDate() {
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based in JS
      const day = String(today.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      recordedErrorLog("Current date getter has failed: ", error);
      return "";
    }
  }

  function getDatePlusDaysMonths(days, months) {
    try {
      const today = new Date();

      today.setMonth(today.getMonth() + months);
      today.setDate(today.getDate() + days);

      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } catch (error) {
      recordedErrorLog("Date plus days getter has failed: ", error);
      return "";
    }
  }

  const handlePackAdd = (id, days, months) => {
    try {
      const endDate = getDatePlusDaysMonths(days, months);

      const payload = {
        start: getCurrentDate(),
        end: endDate,
        authorised_by: currentUser.id,
        user_id: userData.id,
        package_id: id,
      };

      api
        // admin: add package to user
        .addPackToUser(authToken, payload)
        .then((response) => {
          setActivePacks((old) => [...old, response.data]);
        })
        .catch((err) => {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH PACKAGE ADD: ${errorDetails}`,
            err,
            true,
            "Unexpected error adding the package. Check console log for more details."
          );
        });
    } catch (error) {
      recordedErrorLog("Package adder handler has failed: ", error);
    }
  };

  const handlePackModifyClick = (userPackageId, days, months) => {
    setPackToModify({ userPackageId, days, months });
    setIsModifyPackOpen(true);
  };

  const handlePackModifyConfirm = () => {
    try {
      const { userPackageId, days, months } = packToModify;
      const endDate = getDatePlusDaysMonths(days, months);
      
      // Find the current package to get all its information
      const currentPack = activePacks.find((pack) => pack.id === userPackageId);
      if (!currentPack) {
        recordedErrorLog("Package not found for modification", null, true, "Package not found.");
        return;
      }

      // Create a copy of the current package and only modify the fields that need to change
      const payload = {
        ...currentPack,
        end: endDate,
        authorised_by: currentUser.id,
      };

      api
        .modifyPackageForUser(authToken, userPackageId, payload)
        .then((response) => {
          // Update the active packages list with the modified package
          setActivePacks((old) => 
            old.map((pack) => 
              pack.id === userPackageId ? response.data : pack
            )
          );
        })
        .catch((err) => {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH PACKAGE MODIFICATION: ${errorDetails}`,
            err,
            true,
            "Unexpected error modifying the package. Check console log for more details."
          );
        });
      
      setIsModifyPackOpen(false);
      setPackToModify(null);
    } catch (error) {
      recordedErrorLog("Package modification handler has failed: ", error);
    }
  };

  const handlePackDeactivateClick = (userPackageId) => {
    setPackToDeactivate(userPackageId);
    setIsDeactivatePackOpen(true);
  };

  const handlePackDeactivateConfirm = () => {
    try {
      api
        .deactivatePackageForUser(authToken, packToDeactivate)
        .then((response) => {
          // Update the active packages list with the modified package
          setActivePacks((old) => 
            old.map((pack) => 
              pack.id === packToDeactivate ? response.data : pack
            )
          );
        })
        .catch((err) => {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH PACKAGE DEACTIVATION: ${errorDetails}`,
            err,
            true,
            "Unexpected error deactivating the package. Check console log for more details."
          );
        });
      
      setIsDeactivatePackOpen(false);
      setPackToDeactivate(null);
    } catch (error) {
      recordedErrorLog("Package deactivation handler has failed: ", error);
    }
  };

  const confirmUserDelete = () => {
    try {
      setIsConfirmOpen(false);
      api
        // admin: delete user
        .deleteUser(authToken, userData.id)
        .then((response) => {
          console.log("User delete success: ", response);
        })
        .catch((err) => {
          const errorDetails = hasProperty(err, "response")
            ? `Backend response: ${JSON.stringify(err.response, null, 2)}`
            : "No backend response available";
          recordedErrorLog(
            `THERE WAS AN ERROR WITH USER DELETE: ${errorDetails}`,
            err,
            true,
            "Unexpected error occurred deleting the user. Check console log for more details."
          );
        });

      // This is kept outside of user delete enpoint handling, because BE can delete the user and still return an error,
      // so in this case it would break the UI.
      handleUserDelete(userData.id);
    } catch (error) {
      recordedErrorLog("User delete confirmation has failed: ", error);
    }
  };

  return (
    <div className="editUser">
      <div className="sectionTitle">Details:</div>
      <div className="detailSection" ref={detailRef}>
        <div className="generalDetails">
          <div className="subSectionTitle">General:</div>
          <UserData
            fullName={fullName}
            setFullName={setFullName}
            email={email}
            setEmail={setEmail}
            emailVerified={userData ? userData.email_confirmed : false}
            newsLetter={newsLetter}
            setNewsletter={setNewsletter}
            dateCreated={userData ? userData.created_on : ""}
          />
        </div>
        <div className="adminDetails">
          <div className="subSectionTitle">Admin:</div>
          <UserDetail
            title={"Elavated Rights:"}
            value={isAdmin}
            setValue={setIsAdmin}
            checkbox
            checkboxLabel="Admin"
          />
          <UserDetail
            title={"Academic Verification:"}
            value={isAcademicVerified}
            setValue={setIsAcademicVerified}
            checkbox
            checkboxLabel="Academic Verified"
          />
          <UserDetail
            title={"Analysis Hours Left:"}
            value={analysisSupportHoursLeft}
            setValue={setAnalysisSupportHoursLeft}
            number
          />
          <UserDetail
            title={"Stripe Payment Allowed:"}
            value={stripePaymentAllowed}
            setValue={setStripePaymentAllowed}
            checkbox
            checkboxLabel="Stripe Payment Allowed"
          />
        </div>
      </div>
      <Button
        variant="contained"
        className="updateButton"
        onClick={() => startUpdate()}
        disabled={changesList === undefined || changesList.length < 1}
      >
        Confirm Changes
      </Button>
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={handleCloseConfirmModal}
        shouldCloseOnOverlayClick={true}
        contentLabel="Fit Modal"
        appElement={detailRef.current}
        style={{
          content: {
            top: modalPlaceAndSize.top,
            left: modalPlaceAndSize.left,
            right: modalPlaceAndSize.right,
            transform: `translate(-50%, -35%)`,
            width: modalPlaceAndSize.width,
            height: modalPlaceAndSize.height,
          },
          overlay: {
            backgroundColor: "transparent",
            zIndex: "100",
          },
        }}
      >
        <ChangesCheck
          changesList={changesList}
          approveChanges={approveChanges}
          reverseChanges={reverseChanges}
        />
      </Modal>
      <div className="divider" />
      <div className="sectionTitle" ref={dangerZoneRef}>
        Danger Zone:
      </div>
      <Button
        variant="contained"
        className="deleteUserButton"
        onClick={() => setIsDelUserOpen(true)}
      >
        Delete User
      </Button>
      <ConfirmationScreen
        onYes={confirmUserDelete}
        onNo={() => setIsDelUserOpen(false)}
        text={"Are you sure you want to DELETE this user?"}
        open={isDelUserOpen}
        setOpen={setIsDelUserOpen}
        element={dangerZoneRef}
      />
      <ConfirmationScreen
        onYes={handlePackModifyConfirm}
        onNo={() => setIsModifyPackOpen(false)}
        text={"ARE YOU SURE??? You are about to modify this user's package!"}
        open={isModifyPackOpen}
        setOpen={setIsModifyPackOpen}
        element={modifyPackRef}
      />
      <ConfirmationScreen
        onYes={handlePackDeactivateConfirm}
        onNo={() => setIsDeactivatePackOpen(false)}
        text={"ARE YOU SURE??? You are about to deactivate this user's package!"}
        open={isDeactivatePackOpen}
        setOpen={setIsDeactivatePackOpen}
        element={deactivatePackRef}
      />
      <div className="divider" />
      <div className="packageSection">
        <div className="sectionTitle">Packages:</div>
        <div className="availablePackages">
          <div className="packagesContainer">
            <div className="subSectionTitle">Active:</div>
            <div className="packages">
              {activePacks.length >= 1 ? (
                activePacks.map((pack) => {
                  const foundPack = getPackageFromList(
                    packages,
                    pack.package_id
                  );
                  return (
                    <Package
                      key={pack.id}
                      title={foundPack.name}
                      description={foundPack.description}
                      price={foundPack.cost}
                      subscription={foundPack.is_subscription}
                      subscription_active={pack.subscription_active?true:false}
                      active
                      expired={pack.end < getCurrentDate()}
                      available={foundPack.available}
                      activeTill={pack.end}
                      id={foundPack.id}
                      userPackageId={pack.id}
                      admin
                      handleModify={handlePackModifyClick}
                      handleDeactivate={handlePackDeactivateClick}
                      modifyRef={modifyPackRef}
                      deactivateRef={deactivatePackRef}
                    />
                  );
                })
              ) : (
                <div className="noPacks">No active packages</div>
              )}
            </div>
          </div>
          <div className="packagesContainer">
            <div className="subSectionTitle">Available:</div>
            <div className="packages">
              {packages.map((pack) => {
                return (
                  <Package
                    key={pack.id}
                    title={pack.name}
                    description={pack.description}
                    price={pack.cost}
                    length={pack.months}
                    length_days={pack.days ? pack.days : 0}
                    subscription={pack.is_subscription}
                    available={pack.available}
                    id={pack.id}
                    admin
                    handleAdd={handlePackAdd}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditUser;
